<script setup lang="ts">
/**
 * PaperStrip — the torn card-stock backing of a shell bar (tab bar, flow
 * header, desktop headers, site nav / footer, mobile menu panel).
 *
 * It is a purely decorative, absolutely-positioned BACKGROUND: the bar's
 * content sits above it unclipped, so focus rings, dropdowns and in-flow
 * panels are never cut by the tear. Only the three stacked layers are
 * clipped by `--paper-torn-t` / `--paper-torn-b`:
 *   shade — the cast shadow, peeking 2px past the tear (clip-path clips
 *           box-shadow, and a `filter` on a fixed bar would re-rasterise on
 *           every scroll frame on low-end Android),
 *   fibre — a 1px lit line along the tear (the torn paper's light core),
 *   stock — the grained sheet itself.
 *
 * Usage: the parent is a positioned stacking context (`relative isolate`,
 * or already `fixed`/`sticky` + `z-*`) — the strip paints at `-z-10`, under
 * the parent's in-flow content. Keep ≥ 6px of padding on the torn side
 * (the tear is ≤ 2.6px deep) so content never sits over a tooth gap.
 */
withDefaults(
  defineProps<{
    /** The torn edge: 'top' for bottom bars, 'bottom' for header strips. */
    edge: 'top' | 'bottom';
    /** Stock the strip is cut from. */
    stock?: 'card' | 'popover' | 'background';
    /** Cast shadow past the tear (fades, e.g. SiteNav only casts once scrolled). */
    shade?: boolean;
    /**
     * Full-width strip: from `md` (768px) up, use the dense `-wide` tear
     * (120 teeth) — the default 36 teeth stretch into a soft wave at 1440px.
     */
    wide?: boolean;
  }>(),
  {
    stock: 'card',
    shade: true,
    wide: false,
  },
);
</script>

<template>
  <span
    aria-hidden="true"
    class="paper-strip pointer-events-none absolute inset-0 -z-10"
    :class="[edge === 'top' ? 'paper-strip--top' : 'paper-strip--bottom', wide && 'paper-strip--wide']"
  >
    <span
      class="paper-strip__layer paper-strip__shade transition-opacity duration-300 motion-reduce:transition-none"
      :class="shade ? 'opacity-100' : 'opacity-0'"
    />
    <span
      class="paper-strip__layer paper-strip__fibre"
      :class="`paper-strip__stock--${stock}`"
    />
    <span
      class="paper-strip__layer paper-strip__stock paper-grain"
      :class="`paper-strip__stock--${stock}`"
    />
  </span>
</template>

<style scoped>
.paper-strip__layer {
  position: absolute;
  inset: 0;
}

/* Torn top (bottom bars): shade 2px above the tear, fibre 1px above it. */
.paper-strip--top .paper-strip__layer {
  clip-path: var(--paper-torn-t);
}
.paper-strip--top .paper-strip__shade {
  top: -2px;
}
.paper-strip--top .paper-strip__fibre {
  top: -1px;
}

/* Full-width strips: the dense tear from md up (phones keep the 36-teeth one). */
@media (min-width: 768px) {
  .paper-strip--wide.paper-strip--top .paper-strip__layer {
    clip-path: var(--paper-torn-t-wide);
  }
  .paper-strip--wide.paper-strip--bottom .paper-strip__layer {
    clip-path: var(--paper-torn-b-wide);
  }
}

/* Torn bottom (header strips): shade 2px below the tear, fibre 1px below. */
.paper-strip--bottom .paper-strip__layer {
  clip-path: var(--paper-torn-b);
}
.paper-strip--bottom .paper-strip__shade {
  bottom: -2px;
}
.paper-strip--bottom .paper-strip__fibre {
  bottom: -1px;
}

.paper-strip__shade {
  background-color: var(--paper-shade);
}

.paper-strip__stock--card {
  background-color: var(--card);
}
.paper-strip__stock--popover {
  background-color: var(--popover);
}
.paper-strip__stock--background {
  background-color: var(--background);
}

/* The lit core of the tear: the strip's own stock + the --paper-edge rim
   colour laid over it as a flat tint — the same lit rim the sheets carry as
   `inset 0 1px 0 var(--paper-edge)`, following the tear. Painted as an inset
   box-shadow that fills the box (the clip-path trims it to the tear), so no
   gradient is involved and no color-mix() is needed for older WebViews. */
.paper-strip__fibre {
  box-shadow: inset 0 0 0 100vmax var(--paper-edge);
}
</style>
